import { PolicyActionEnum } from '../actions.js';
import { type BuiltinPolicyDefinition, defineBuiltinPolicy } from './types.js';

/**
 * InsightEdge built-in policies, per resource and level:
 * - Read Only: the resource's View and tab-view actions. Not View unpublished: viewers see what
 *   is published.
 * - Manage: Read Only plus View unpublished and everything that creates, changes or deletes the
 *   resource, except Publish.
 * - Publish: Publish on its own, with the views it needs (View unpublished, and View where the
 *   resource has one), so it can be granted separately from editing.
 *
 * Every policy holds the actions its own actions require, so none trips the editor's warnings.
 * File, Brand and Folder have few actions, and Folder only has Manage, so no Read Only for it.
 */

const projectReadOnly = [
  PolicyActionEnum.IeProjectViewEntities,
  PolicyActionEnum.IeProjectViewHighlights,
  PolicyActionEnum.IeProjectViewTags,
  PolicyActionEnum.IeProjectViewFilesTab,
  PolicyActionEnum.IeProjectViewSummary,
  PolicyActionEnum.IeProjectViewWidgets,
  PolicyActionEnum.IeProjectViewSurveyResponses,
];
const projectManage = [
  ...projectReadOnly,
  PolicyActionEnum.IeProjectCreate,
  PolicyActionEnum.IeProjectManageDetails,
  PolicyActionEnum.IeProjectManageGroupTags,
  PolicyActionEnum.IeProjectManageEntities,
  PolicyActionEnum.IeProjectCreateTags,
  PolicyActionEnum.IeProjectEditTags,
  PolicyActionEnum.IeProjectDeleteTags,
  PolicyActionEnum.IeProjectRefreshSummary,
  PolicyActionEnum.IeProjectAddWidgets,
  PolicyActionEnum.IeProjectUpdateWidgets,
  PolicyActionEnum.IeProjectDeleteWidgets,
];

const fileReadOnly = [PolicyActionEnum.IeFileViewSummary];
const fileManage = [
  ...fileReadOnly,
  PolicyActionEnum.IeFileViewUnpublished,
  PolicyActionEnum.IeFileUpload,
  PolicyActionEnum.IeFileManageGroupTags,
  PolicyActionEnum.IeFileRefreshSummary,
  PolicyActionEnum.IeFileDelete,
];
const filePublish = [PolicyActionEnum.IeFileViewUnpublished, PolicyActionEnum.IeFilePublish];

const brandReadOnly = [
  PolicyActionEnum.IeBrandViewFilesTab,
  PolicyActionEnum.IeBrandViewProjectsTab,
  PolicyActionEnum.IeBrandViewProjectTrackerTab,
];
const brandManage = [...brandReadOnly, PolicyActionEnum.IeBrandCreate, PolicyActionEnum.IeBrandDelete];

const folderManage = [PolicyActionEnum.IeFolderManageGroupTags, PolicyActionEnum.IeFolderDelete];

const quantAnalysisReadOnly = [PolicyActionEnum.IeQuantAnalysisView];
const quantAnalysisManage = [
  ...quantAnalysisReadOnly,
  PolicyActionEnum.IeQuantAnalysisViewUnpublished,
  PolicyActionEnum.IeQuantAnalysisEdit,
  PolicyActionEnum.IeQuantAnalysisDelete,
];
const quantAnalysisPublish = [
  ...quantAnalysisReadOnly,
  PolicyActionEnum.IeQuantAnalysisViewUnpublished,
  PolicyActionEnum.IeQuantAnalysisPublish,
];

const segmentReadOnly = [PolicyActionEnum.IeSegmentView];
const segmentManage = [
  ...segmentReadOnly,
  PolicyActionEnum.IeSegmentViewUnpublished,
  PolicyActionEnum.IeSegmentCreate,
  PolicyActionEnum.IeSegmentEdit,
  PolicyActionEnum.IeSegmentDelete,
];
const segmentPublish = [
  ...segmentReadOnly,
  PolicyActionEnum.IeSegmentViewUnpublished,
  PolicyActionEnum.IeSegmentPublish,
];

const patientJourneyReadOnly = [PolicyActionEnum.IePatientJourneyView];
const patientJourneyManage = [
  ...patientJourneyReadOnly,
  PolicyActionEnum.IePatientJourneyViewUnpublished,
  PolicyActionEnum.IePatientJourneyManage,
  PolicyActionEnum.IePatientJourneyCreate,
  PolicyActionEnum.IePatientJourneyDelete,
];
const patientJourneyPublish = [
  ...patientJourneyReadOnly,
  PolicyActionEnum.IePatientJourneyViewUnpublished,
  PolicyActionEnum.IePatientJourneyPublish,
];

const dashboardReadOnly = [PolicyActionEnum.IeDashboardView];
const dashboardManage = [
  ...dashboardReadOnly,
  PolicyActionEnum.IeDashboardViewUnpublished,
  PolicyActionEnum.IeDashboardCreate,
  PolicyActionEnum.IeDashboardEdit,
  PolicyActionEnum.IeDashboardDelete,
];
const dashboardPublish = [
  ...dashboardReadOnly,
  PolicyActionEnum.IeDashboardViewUnpublished,
  PolicyActionEnum.IeDashboardPublish,
];

const globalSearchReadOnly = [PolicyActionEnum.IeGlobalSearchView];

const transcriptReadOnly = [
  PolicyActionEnum.IeTranscriptViewSummary,
  PolicyActionEnum.IeTranscriptViewEntities,
  PolicyActionEnum.IeTranscriptViewHighlights,
  PolicyActionEnum.IeTranscriptViewTaggedMoments,
  PolicyActionEnum.IeTranscriptViewComments,
];
const transcriptManage = [
  ...transcriptReadOnly,
  PolicyActionEnum.IeTranscriptViewUnpublished,
  PolicyActionEnum.IeTranscriptManageGroupTags,
  PolicyActionEnum.IeTranscriptMarkGoodQuotes,
  PolicyActionEnum.IeTranscriptApplyModifications,
  PolicyActionEnum.IeTranscriptRefreshSummary,
  PolicyActionEnum.IeTranscriptManageEntities,
  PolicyActionEnum.IeTranscriptCreateHighlights,
  PolicyActionEnum.IeTranscriptEditHighlights,
  PolicyActionEnum.IeTranscriptDeleteHighlights,
  PolicyActionEnum.IeTranscriptCreateTaggedMoments,
  PolicyActionEnum.IeTranscriptEditTaggedMoments,
  PolicyActionEnum.IeTranscriptDeleteTaggedMoments,
  PolicyActionEnum.IeTranscriptComment,
  PolicyActionEnum.IeTranscriptEditComments,
  PolicyActionEnum.IeTranscriptDeleteComments,
];
const transcriptPublish = [
  PolicyActionEnum.IeTranscriptViewUnpublished,
  PolicyActionEnum.IeTranscriptPublish,
];

type Level = 'read-only' | 'manage' | 'publish';

const LEVEL_NAMES: Record<Level, string> = {
  'read-only': 'Read Only',
  manage: 'Manage',
  publish: 'Publish',
};

const LEVEL_DESCRIPTIONS: Record<Level, string> = {
  'read-only': 'View',
  manage: 'View and change',
  publish: 'Publish',
};

function resourcePolicy(
  resource: { slug: string; name: string; plural: string },
  level: Level,
  actions: readonly PolicyActionEnum[]
): BuiltinPolicyDefinition {
  return defineBuiltinPolicy({
    key: `builtin:ie-${resource.slug}-${level}`,
    name: `InsightEdge ${resource.name} ${LEVEL_NAMES[level]}`,
    description: `${LEVEL_DESCRIPTIONS[level]} InsightEdge ${resource.plural}.`,
    actions,
  });
}

const project = { slug: 'project', name: 'Project', plural: 'projects' };
const file = { slug: 'file', name: 'File', plural: 'files' };
const brand = { slug: 'brand', name: 'Brand', plural: 'brands' };
const folder = { slug: 'folder', name: 'Folder', plural: 'folders' };
const quantAnalysis = { slug: 'quant-analysis', name: 'Quant Analysis', plural: 'quant analyses' };
const segment = { slug: 'segment', name: 'Segment', plural: 'segments' };
const patientJourney = { slug: 'patient-journey', name: 'Patient Journey', plural: 'patient journeys' };
const dashboard = { slug: 'dashboard', name: 'Dashboard', plural: 'dashboards' };
const transcript = { slug: 'transcript', name: 'Transcript', plural: 'transcripts' };
const globalSearch = { slug: 'global-search', name: 'Global Search', plural: 'global search' };

const insightEdgeReadOnlyPolicies: readonly BuiltinPolicyDefinition[] = [
  resourcePolicy(project, 'read-only', projectReadOnly),
  resourcePolicy(file, 'read-only', fileReadOnly),
  resourcePolicy(brand, 'read-only', brandReadOnly),
  resourcePolicy(quantAnalysis, 'read-only', quantAnalysisReadOnly),
  resourcePolicy(segment, 'read-only', segmentReadOnly),
  resourcePolicy(patientJourney, 'read-only', patientJourneyReadOnly),
  resourcePolicy(dashboard, 'read-only', dashboardReadOnly),
  resourcePolicy(transcript, 'read-only', transcriptReadOnly),
  resourcePolicy(globalSearch, 'read-only', globalSearchReadOnly),
];

const insightEdgeManagePolicies: readonly BuiltinPolicyDefinition[] = [
  resourcePolicy(project, 'manage', projectManage),
  resourcePolicy(file, 'manage', fileManage),
  resourcePolicy(brand, 'manage', brandManage),
  resourcePolicy(folder, 'manage', folderManage),
  resourcePolicy(quantAnalysis, 'manage', quantAnalysisManage),
  resourcePolicy(segment, 'manage', segmentManage),
  resourcePolicy(patientJourney, 'manage', patientJourneyManage),
  resourcePolicy(dashboard, 'manage', dashboardManage),
  resourcePolicy(transcript, 'manage', transcriptManage),
];

const insightEdgePublishPolicies: readonly BuiltinPolicyDefinition[] = [
  resourcePolicy(file, 'publish', filePublish),
  resourcePolicy(quantAnalysis, 'publish', quantAnalysisPublish),
  resourcePolicy(segment, 'publish', segmentPublish),
  resourcePolicy(patientJourney, 'publish', patientJourneyPublish),
  resourcePolicy(dashboard, 'publish', dashboardPublish),
  resourcePolicy(transcript, 'publish', transcriptPublish),
];

const unique = (actions: readonly PolicyActionEnum[]): PolicyActionEnum[] => [...new Set(actions)];

/** Every InsightEdge view action, across all resources. */
export const insightEdgeReadOnlyPolicy = defineBuiltinPolicy({
  key: 'builtin:ie-read-only',
  name: 'InsightEdge Read Only',
  description: 'View everything in InsightEdge.',
  actions: unique([
    ...projectReadOnly,
    ...fileReadOnly,
    ...brandReadOnly,
    ...quantAnalysisReadOnly,
    ...segmentReadOnly,
    ...patientJourneyReadOnly,
    ...dashboardReadOnly,
    ...transcriptReadOnly,
    ...globalSearchReadOnly,
  ]),
});

/** Every InsightEdge action, including publishing. */
export const insightEdgeFullAccessPolicy = defineBuiltinPolicy({
  key: 'builtin:ie-full-access',
  name: 'InsightEdge Full Access',
  description: 'View, change and publish everything in InsightEdge.',
  actions: unique([
    ...projectManage,
    ...fileManage,
    ...filePublish,
    ...brandManage,
    ...folderManage,
    ...quantAnalysisManage,
    ...quantAnalysisPublish,
    ...segmentManage,
    ...segmentPublish,
    ...patientJourneyManage,
    ...patientJourneyPublish,
    ...dashboardManage,
    ...dashboardPublish,
    ...transcriptManage,
    ...transcriptPublish,
    ...globalSearchReadOnly,
  ]),
});

export const INSIGHTEDGE_POLICIES: readonly BuiltinPolicyDefinition[] = [
  insightEdgeReadOnlyPolicy,
  insightEdgeFullAccessPolicy,
  ...insightEdgeReadOnlyPolicies,
  ...insightEdgeManagePolicies,
  ...insightEdgePublishPolicies,
];
